import React from 'react';
import { NavLink } from 'react-router-dom';

import {
  Chip,
  Collapse,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  Tooltip,
  styled,
  lighten,
} from '@mui/material';
import { rgba } from 'polished';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ResourceDefinition } from 'react-admin';
import theme from '../../../theme';

const Title = styled(ListItemText)(({ depth }: { depth: number }) => ({
  margin: 0,
  '& span': {
    color: rgba(theme.customSidebar.color, depth && depth > 0 ? 0.7 : 1),
    fontSize: theme.typography.body1.fontSize,
    padding: `0 ${theme.spacing(4)}`,
  },
}));

const Badge = styled(Chip)(() => ({
  fontWeight: theme.typography.fontWeightBold,
  height: 20,
  position: 'absolute',
  right: 26,
  top: 12,
  background: theme.customSidebar.badge.background,
  zIndex: 1,
  '& span.MuiChip-label, span.MuiChip-label:hover': {
    fontSize: 11,
    cursor: 'pointer',
    color: theme.customSidebar.badge.color,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.customSidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.customSidebar.color, 0.5)};
`;

// Inactive icons stay muted. The current page gets a white left indicator, a
// lighter brand surface and a full-white icon, in both expanded and collapsed mode.
const itemSx = (depth: number, collapsed: boolean): SxProps<Theme> => ({
  paddingTop: theme.spacing(depth && depth > 0 ? 2 : 3),
  paddingBottom: theme.spacing(depth && depth > 0 ? 2 : 3),
  paddingLeft: theme.spacing(depth && depth > 0 ? 14 : 8),
  paddingRight: theme.spacing(depth && depth > 0 ? 4 : 7),
  ...(collapsed ? { justifyContent: 'center', paddingLeft: 0, paddingRight: 0 } : {}),
  fontWeight: theme.typography.fontWeightRegular,
  '& svg': {
    color: theme.customSidebar.color,
    fontSize: 20,
    width: 25,
    height: 25,
    opacity: 0.5,
  },
  ':hover': {
    background: lighten(theme.customSidebar.background, 0.05),
    color: theme.customSidebar.color,
  },
  '&.active': {
    backgroundColor: lighten(theme.customSidebar.background, 0.10),
    boxShadow: `inset 3px 0 0 ${theme.palette.common.white}`,
    '& svg': {
      color: theme.palette.common.white,
      opacity: 1,
    },
    '& span': {
      color: theme.customSidebar.color,
    },
  },
});

const SidebarNavListItem = ({
  title,
  href,
  openNewTab = false,
  depth = 0,
  children,
  icon: Icon,
  badge,
  exact,
  open: openProp = false,
  collapsed = false,
  onClose,
}: {
  title: string;
  href: string;
  openNewTab?: boolean;
  depth: number;
  children?: React.ReactNode;
  icon: React.ElementType;
  badge: ResourceDefinition['icon'];
  exact: boolean;
  open?: boolean;
  collapsed?: boolean;
  onClose?: () => void;
}) => {
  const [open, setOpen] = React.useState(openProp);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  if (collapsed) {
    return (
      <Tooltip title={title} placement="right">
        <ListItemButton
          component={NavLink}
          to={href}
          target={openNewTab ? '_blank' : undefined}
          end={exact}
          onClick={onClose}
          aria-label={title}
          sx={itemSx(depth, collapsed)}
        >
          {Icon && <Icon />}
        </ListItemButton>
      </Tooltip>
    );
  }

  if (children) {
    return (
      <>
        <ListItemButton
          component={NavLink}
          to={href}
          onClick={handleToggle}
          sx={itemSx(depth, collapsed)}
        >
          {Icon && <Icon />}
          <Title depth={depth}>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
        <Collapse in={open}>{children}</Collapse>
      </>
    );
  }

  return (
    <ListItemButton
      component={NavLink}
      to={href}
      target={openNewTab ? '_blank' : undefined}
      end={exact}
      onClick={onClose}
      sx={itemSx(depth, collapsed)}
    >
      {Icon && <Icon />}
      <Title depth={depth}>
        {title}
        {badge && <Badge label={badge} />}
      </Title>
    </ListItemButton>
  );
};

export default SidebarNavListItem;
